import { useState } from 'react'
import type { FormEvent } from 'react'
import { api, useAuth } from '@shop/api-client'
import { Banner, Button, ErrorBanner, Field, TextInput } from '@shop/ui'

/** أدنى طولٍ — <b>يطابق `AuthenticationService.MinimumPasswordLength`</b>. */
const MINIMUM = 10

/**
 * تغييرُ الكلمة المؤقّتة — <b>وهي شاشةٌ إلزاميّةٌ لا تُتخطّى</b>.
 *
 * كلُّ حسابٍ يُنشئه المالك يُولد بكلمةٍ يعرفها المالك، فالموظّفُ الذي لا يغيّرها
 * يعمل بحسابٍ لغيره مفتاحُه — ويسقط معنى «من فعل هذا» في التدقيق كلِّه. ولهذا
 * لا زرَّ «لاحقًا» هنا، ولا طريقَ إلى الشاشات قبلها (انظر `App.tsx`).
 *
 * <b>والتحقّقُ هنا لا يُغني عن تحقّق الخادم</b> ولا يدّعي ذلك: هو ليقرأ الموظّفُ
 * الشرطَ قبل أن يضغط، والحارسُ الحقيقيّ في `ChangePasswordAsync`.
 */
export default function ChangePassword({ forced }: { forced: boolean }) {
  const { passwordChanged, logout } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  const tooShort = next.length > 0 && next.length < MINIMUM
  const mismatch = again.length > 0 && next !== again
  const ready = current.length > 0 && next.length >= MINIMUM && next === again

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await api<void>('/api/v1/auth/change-password', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      })

      // لا كلمةَ تبقى في الحالة بعد النجاح.
      setCurrent('')
      setNext('')
      setAgain('')
      setDone(true)
      passwordChanged()
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <form
        className="w-full max-w-sm space-y-4 rounded-lg border border-line bg-surface-raised p-6"
        onSubmit={submit}
      >
        <h1 className="text-lg font-semibold">تغيير كلمة المرور</h1>

        {forced && (
          <Banner tone="warn">
            دخلتَ بكلمةٍ مؤقّتةٍ يعرفها من أنشأ حسابك. غيّرها الآن — كلُّ ما تُدخله
            بعدها يُنسب إليك وحدَك.
          </Banner>
        )}

        {done && <Banner tone="ok">تغيّرت الكلمة. وأُخرجت أجهزتُك الأخرى، وجهازُك هذا باقٍ.</Banner>}

        <Field label="الكلمة الحاليّة">
          <TextInput
            value={current}
            onChange={setCurrent}
            type="password"
            autoComplete="current-password"
            dir="ltr"
            autoFocus
            disabled={busy}
          />
        </Field>

        <Field label="الكلمة الجديدة" hint={`${MINIMUM} محارفَ فأكثر — والطولُ وحدَه، اختر ما تتذكّره.`}>
          <TextInput
            value={next}
            onChange={setNext}
            type="password"
            autoComplete="new-password"
            dir="ltr"
            disabled={busy}
          />
        </Field>

        <Field label="أعِد كتابتها">
          <TextInput
            value={again}
            onChange={setAgain}
            type="password"
            autoComplete="new-password"
            dir="ltr"
            disabled={busy}
          />
        </Field>

        {tooShort && <Banner tone="warn">الكلمةُ أقصرُ من {MINIMUM} محارف.</Banner>}
        {mismatch && <Banner tone="warn">النسختان غيرُ متطابقتين.</Banner>}

        <ErrorBanner error={error} />

        <Button type="submit" tone="primary" wide disabled={busy || !ready}>
          {busy ? 'يُحفظ…' : 'حفظ الكلمة الجديدة'}
        </Button>

        {forced && (
          <Button onClick={() => void logout()} wide disabled={busy}>
            خروج
          </Button>
        )}
      </form>
    </div>
  )
}
